import { 
  Heart, Home, Pill, ShoppingBag, Calendar, 
  FileText, Eye, Users, Video, UserPlus,
  MessageCircle, TrendingUp
} from 'lucide-react';
import EnhancedSEO from '@/components/seo/EnhancedSEO';
import HeroSectionB from '@/components/older-adults-b/HeroSectionB';
import WhatIsThriveMaxSection from '@/components/older-adults-b/WhatIsThriveMaxSection';
import HowItWorksSectionB from '@/components/older-adults-b/HowItWorksSectionB';
import FeatureBlocksSection from '@/components/older-adults-b/FeatureBlocksSection';
import PrivacySectionB from '@/components/older-adults-b/PrivacySectionB';
import CTASectionB from '@/components/older-adults-b/CTASectionB';
import TestimonialsSection from '@/components/older-adults/TestimonialsSection';
import BridgeToFeaturesSection from '@/components/older-adults-b/BridgeToFeaturesSection';

const OlderAdults = () => {
  const testimonials = [
    {
      name: "Margaret S., 72",
      content: "The vitals tracker helped me catch high blood pressure early, and the medication reminders ensure I never miss a dose. My doctor is impressed with how organized my health records are now!"
    },
    {
      name: "Robert T., 68", 
      content: "I love the brain health games and photo gallery features. Sharing memories with my grandchildren through the platform brings such joy, and the cognitive exercises keep me sharp."
    },
    {
      name: "Dorothy L., 75",
      content: "The food delivery support and recipes hub have been lifesavers! I can order healthy meals easily and try new recipes. The pet care tools help me take better care of my cat Whiskers too."
    }
  ];

  const thriveMaxFeatures = [
    { icon: Heart, title: "Health & Wellness Tracking", description: "Blood pressure, weight, symptoms, sleep, and mood—all in one place." },
    { icon: Pill, title: "Medication Management", description: "Smart reminders + FDA database to understand your medications." },
    { icon: Home, title: "Unlimited Home Safety Scans", description: "Powered by ThriveVision to spot fall risks before they become problems." },
    { icon: ShoppingBag, title: "DIY Fixes & Product Links", description: "Get fix-it tips, videos, and curated product recommendations." },
    { icon: TrendingUp, title: "Personal ThriveScore", description: "One number that shows your overall wellness and progress." },
    { icon: Calendar, title: "Google & Apple Calendar Sync", description: "Appointments visible across all your devices." },
    { icon: FileText, title: "Daily Living & Life Skills Checks", description: "Track daily living activities, mobility, and fall risk." },
    { icon: Eye, title: "Vision & Driving Safety", description: "Self-assessments to help you stay independent and safe." },
    { icon: Users, title: "The Village Community", description: "Connect with peers, share experiences, and find local activities." },
    { icon: Video, title: "Built-In Video Calls", description: "FaceTime and Google Meet integration for easy connection." },
    { icon: UserPlus, title: "Invite 1 Family Member", description: "Share your progress with someone you trust." },
    { icon: MessageCircle, title: "6 Chat Companions", description: "Friendly guidance on health, medications, activities—anytime." }
  ];

  return (
    <>
      <EnhancedSEO 
        title="ThriveMax for Older Adults - Your Independence at Home Starts Here"
        description="ThriveMax gives you room-by-room safety insights, daily check-ins, and wellness tracking to stay independent at home longer. See how it works."
        keywords="ThriveMax, older adults, aging in place, senior independence, home safety, wellness tracking, senior community, thrive at home"
        canonical="https://seniorthrive.com/older-adults"
        breadcrumbs={[
          { name: "Home", url: "https://seniorthrive.com/" },
          { name: "ThriveMax for Older Adults", url: "https://seniorthrive.com/older-adults" }
        ]}
        schema={{
          type: "Service",
          name: "ThriveMax for Older Adults",
          description: "ThriveMax: Complete wellness and safety platform for independent living at home",
          provider: "SeniorThrive"
        }}
      />
      
      <HeroSectionB />
      
      <div className="px-4">
        <div className="container mx-auto max-w-6xl">
          
          <WhatIsThriveMaxSection />
          
          <HowItWorksSectionB />
          
          <FeatureBlocksSection />
          
          {/* ThriveMax Features Grid */}
          <section className="py-16 lg:py-20">
            <div className="text-center mb-12">
              <h2 className="text-2xl lg:text-3xl font-bold text-foreground mb-2">
                What's Included in <span className="text-primary">ThriveMax</span>
              </h2>
              <p className="text-lg text-muted-foreground mb-4">
                SeniorThrive's Full Plan
              </p>
              <p className="text-sm text-muted-foreground/80 max-w-xl mx-auto">
                SeniorThrive is the system. ThriveMax is the plan that unlocks the full experience.
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {thriveMaxFeatures.map((feature, i) => (
                <div 
                  key={i} 
                  className="flex items-start gap-3 p-4 rounded-xl border border-border/50 bg-background/50 hover:border-primary/20 hover:bg-primary/[0.02] transition-all"
                >
                  <div className="w-9 h-9 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <feature.icon className="w-4.5 h-4.5 text-primary" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h3 className="font-medium text-foreground text-sm mb-0.5">{feature.title}</h3>
                    <p className="text-xs text-muted-foreground leading-relaxed">{feature.description}</p>
                  </div>
                </div>
              ))}
            </div>
            
            {/* CTA */}
            <div className="text-center mt-10">
              <a 
                href="/pricing" 
                className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3 rounded-full font-medium hover:bg-primary/90 transition-colors shadow-lg shadow-primary/20"
              >
                Start ThriveMax Today
              </a>
            </div>
          </section>
          
          <PrivacySectionB />
          
          <TestimonialsSection testimonials={testimonials} />
          
          <BridgeToFeaturesSection />
          
          <CTASectionB />
          
          {/* Bottom spacing */}
          <div className="h-8" />
        </div>
      </div>
    </>
  );
};

export default OlderAdults;
